import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  PanelLeft,
  Home,
  MessageSquare,
  Settings,
  Briefcase,
  Wrench,
  Bot,
  Package,
  Code,
  FileText,
  Search,
  Edit,
  Hash,
  Lock,
  Clock,
  Globe,
  GitBranch,
  Calendar,
  FolderOpen,
  Ticket,
  Users,
  Timer,
  CheckSquare,
  Activity,
  Monitor,
  Rocket,
  Send,
  DollarSign,
  Calculator,
  PiggyBank,
  Receipt,
  TrendingUp,
  BarChart3,
  PieChart,
  LineChart,
  Heart,
  Smile,
  Coffee,
  Dumbbell,
  BookOpen,
  Brain,
  GraduationCap,
  Award,
  Bookmark,
  UserPlus,
  MessageCircle,
  Car,
  PartyPopper,
  Shield,
  Key,
  Smartphone,
  Moon,
  User,
  Bell,
  Zap,
  Sparkles,
  Cpu,
  Workflow,
  Atom,
  SquareCode,
  Plug,
  LayoutDashboard,
  Terminal,
  Puzzle,
  Server,
  Radio,
  FileCode,
  Boxes,
  Megaphone,
  Vault,
  X,
  LogOut,
} from 'lucide-react';
import { useMenuConfig } from '../hooks/useMenuConfig.ts';
import PrimarySidebarNav from './dashboard/PrimarySidebarNav.tsx';
import TopBar from './TopBar.tsx';
import CostBudgetPopover from './AIConfig/CostBudgetPopover.jsx';
import useCostSummary from '../hooks/useCostSummary.ts';
import RunDrawer from './agentsWorkflows/RunDrawer.jsx';
import RuntimeDock from './agentsWorkflows/RuntimeDock.tsx';
import { customAgentAPI, remindersAPI, agentRunAPI } from '../services/api';
import { useRun } from '../context/RunContext.tsx';
import { getCurrentUserProfile, formatUserProfile } from '../utils/auth.ts';

const iconMap: Record<string, any> = {
  Home,
  MessageSquare,
  Settings,
  Briefcase,
  Wrench,
  Bot,
  Package,
  Code,
  FileText,
  Search,
  Edit,
  Hash,
  Lock,
  Clock,
  Globe,
  GitBranch,
  Calendar,
  FolderOpen,
  Ticket,
  Users,
  Timer,
  CheckSquare,
  Activity,
  Monitor,
  Rocket,
  Send,
  DollarSign,
  Calculator,
  PiggyBank,
  Receipt,
  TrendingUp,
  BarChart3,
  PieChart,
  LineChart,
  Heart,
  Smile,
  Coffee,
  Dumbbell,
  BookOpen,
  Brain,
  GraduationCap,
  Award,
  Bookmark,
  UserPlus,
  MessageCircle,
  Car,
  PartyPopper,
  Shield,
  Key,
  Smartphone,
  Moon,
  User,
  Bell,
  Zap,
  Sparkles,
  Cpu,
  Workflow,
  Atom,
  SquareCode,
  Plug,
  LayoutDashboard,
  Terminal,
  Puzzle,
  Server,
  Radio,
  FileCode,
  Boxes,
  Megaphone,
  Vault,
};

interface MenuItem {
  label: string;
  path?: string;
  icon: string;
  visible: boolean;
  uncollapsible?: boolean;
  children?: Record<string, MenuItem>;
}

interface LayoutProps {
  children: React.ReactNode;
  onLogout?: () => void;
}

export default function Layout({ children, onLogout }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const MOBILE_BREAKPOINT = 768;
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= MOBILE_BREAKPOINT;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(true);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [currency, setCurrency] = useState(localStorage.getItem('ai_currency') || 'USD');
  const { data: costData, loading: costLoading, hasData: costHasData, refresh: fetchCostSummary } = useCostSummary();
  const [showMobileCostPop, setShowMobileCostPop] = useState(false);
  const [showMobileReminders, setShowMobileReminders] = useState(false);
  const [mobileReminders, setMobileReminders] = useState<any[]>([]);
  const [mobileRemindersLoading, setMobileRemindersLoading] = useState(false);
  const [mobileSeenReminderKeys, setMobileSeenReminderKeys] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('lyra_seen_reminders');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });
  const [mobileStoppingRunIds, setMobileStoppingRunIds] = useState<Set<string>>(new Set());
  const mobileCostBtnRef = React.useRef<HTMLButtonElement | null>(null);

  const [isMenuCollapsed, setIsMenuCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('menuCollapsed');
      return saved ? JSON.parse(saved) : false;
    } catch (e) {
      return false;
    }
  });

  const toggleMenuCollapsed = () => {
    setIsMenuCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('menuCollapsed', JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  };

  const [runDrawerOpen, setRunDrawerOpen] = useState(false);
  const [runDrawerAgents, setRunDrawerAgents] = useState<any[]>([]);
  const [runDrawerMinimized, setRunDrawerMinimized] = useState(false);
  const [runDrawerRunning, setRunDrawerRunning] = useState(false);
  const [runDrawerSpaceId, setRunDrawerSpaceId] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    return savedDarkMode ? JSON.parse(savedDarkMode) : false;
  });

  const { activeRuns, stopRun } = useRun();
  const runningCount = activeRuns.filter((r) => r.status === 'running' || r.status === 'queued').length;
  const userProfile = getCurrentUserProfile();
  const formattedProfile = userProfile ? formatUserProfile(userProfile) : null;

  const { visibleMenuItems } = useMenuConfig();

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', JSON.stringify(newDarkMode));

    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  React.useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  React.useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  React.useEffect(() => {
    // Keep mobile navigation expanded so labels are always visible.
    if (isMobile && isMenuCollapsed) {
      setIsMenuCollapsed(false);
    }
  }, [isMobile, isMenuCollapsed]);

  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!isMobileMenuOpen) {
      setMobileSearchQuery('');
      setShowMobileCostPop(false);
      setShowMobileReminders(false);
    }
  }, [isMobileMenuOpen]);

  React.useEffect(() => {
    localStorage.setItem('ai_currency', currency);
  }, [currency]);

  React.useEffect(() => {
    if (!isMobile || !isMobileMenuOpen) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onEsc);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onEsc);
    };
  }, [isMobile, isMobileMenuOpen]);

  React.useEffect(() => {
    let lastOpenTs = 0;

    const onOpen = () => {
      try {
        if (document.querySelector('[data-lyra-run-drawer-root]')) return;
      } catch (_) {}
      lastOpenTs = Date.now();
      setRunDrawerOpen(true);
      setRunDrawerMinimized(false);
    };

    const onClose = () => {
      try {
        if (Date.now() - lastOpenTs < 300) return;
      } catch (_) {}
      setRunDrawerOpen(false);
      setRunDrawerMinimized(false);
      setRunDrawerRunning(false);
    };

    const onMinimized = () => {
      setRunDrawerOpen(true);
      setRunDrawerMinimized(true);
    };
    const onRestore = () => {
      setRunDrawerOpen(true);
      setRunDrawerMinimized(false);
    };
    const onRunStart = () => setRunDrawerRunning(true);
    const onRunEnd = () => setRunDrawerRunning(false);

    window.addEventListener('lyra:run-open', onOpen as any);
    window.addEventListener('lyra:run-close', onClose as any);
    window.addEventListener('lyra:run-minimized', onMinimized as any);
    window.addEventListener('lyra:run-restore', onRestore as any);
    window.addEventListener('lyra:run-start', onRunStart as any);
    window.addEventListener('lyra:run-end', onRunEnd as any);

    const onRequestOpen = async (e: any) => {
      const agentId = e?.detail?.agentId || null;
      const spaceId = e?.detail?.spaceId || null;
      const prepopulateText = e?.detail?.prepopulateText || null;
      const prefillAgent = e?.detail?.prefillAgent || null;

      if (prefillAgent && !agentId) {
        try {
          const res = await customAgentAPI.list().catch(() => []);
          const list = Array.isArray(res) ? res : res?.agents || [];
          const synthetic = {
            _id: `catalogue:${prefillAgent.id || Date.now()}`,
            id: `catalogue:${prefillAgent.id || Date.now()}`,
            name: prefillAgent.name,
            description: prefillAgent.description || '',
            systemPrompt: prefillAgent.systemPrompt || '',
            defaultModel: prefillAgent.defaultModel || 'auto',
            category: prefillAgent.category || 'general',
            isCatalogue: true,
          };
          const merged = [synthetic, ...list];
          setRunDrawerAgents(merged);
          setRunDrawerSpaceId(spaceId);
          setRunDrawerOpen(true);
          setRunDrawerMinimized(false);
          try {
            window.dispatchEvent(new CustomEvent('lyra:run-open'));
          } catch (_) {}
          const synthId = synthetic._id;
          try {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('lyra:set-run-target', { detail: { agentId: synthId, prepopulateText } }));
            }, 60);
          } catch (_) {}
        } catch (_) {
          setRunDrawerOpen(true);
          try {
            window.dispatchEvent(new CustomEvent('lyra:run-open'));
          } catch (_2) {}
        }
        return;
      }

      try {
        const res = await customAgentAPI.list();
        const list = Array.isArray(res) ? res : res?.agents || [];
        setRunDrawerAgents(list);
        setRunDrawerSpaceId(spaceId);
        try {
          sessionStorage.removeItem('lyra:runDrawerState:v1');
        } catch (_) {}

        const existingRoot = typeof document !== 'undefined' && document.querySelector && document.querySelector('[data-lyra-run-drawer-root]');
        if (existingRoot) {
          setRunDrawerAgents(list);
          setRunDrawerSpaceId(spaceId);
          setRunDrawerOpen(true);
          setRunDrawerMinimized(false);
          try {
            window.dispatchEvent(new CustomEvent('lyra:run-restore'));
          } catch (_) {}
          if (agentId || prepopulateText) {
            try {
              window.dispatchEvent(new CustomEvent('lyra:set-run-target', { detail: { agentId, prepopulateText } }));
            } catch (_) {}
          }
          return;
        }

        if (agentId) {
          const found = list.find((a) => (a._id || a.id) === agentId);
          if (found) {
            setRunDrawerOpen(true);
            try {
              window.dispatchEvent(new CustomEvent('lyra:run-open'));
            } catch (_) {}
            try {
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('lyra:set-run-target', { detail: { agentId, prepopulateText } }));
              }, 60);
            } catch (_) {}
            return;
          }
        }

        setRunDrawerOpen(true);
        try {
          window.dispatchEvent(new CustomEvent('lyra:run-open'));
        } catch (_) {}
        if (prepopulateText && !agentId) {
          try {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('lyra:set-run-target', { detail: { prepopulateText } }));
            }, 60);
          } catch (_) {}
        }
      } catch (err) {
        setRunDrawerOpen(false);
      }
    };

    window.addEventListener('lyra:open-run-drawer', onRequestOpen as any);
    return () => {
      window.removeEventListener('lyra:run-open', onOpen as any);
      window.removeEventListener('lyra:run-close', onClose as any);
      window.removeEventListener('lyra:run-minimized', onMinimized as any);
      window.removeEventListener('lyra:run-restore', onRestore as any);
      window.removeEventListener('lyra:run-start', onRunStart as any);
      window.removeEventListener('lyra:run-end', onRunEnd as any);
      window.removeEventListener('lyra:open-run-drawer', onRequestOpen as any);
    };
  }, []);

  const handleTopBarIconClick = (iconName: string) => {
    switch (iconName) {
      case 'customize':
        navigate('/settings/menu-customization');
        break;
      case 'profile':
        navigate('/settings/profile');
        break;
      default:
        break;
    }
  };

  const handleSearchResults = (results: any[]) => {
    setSearchResults(results);
  };

  const mobileQuickActions = [];

  const handleMobileQuickAction = (action: string) => {
    switch (action) {
      case 'profile':
        navigate('/settings/profile');
        setIsMobileMenuOpen(false);
        break;
      default:
        break;
    }
  };

  const refreshMobileReminders = async (loadingState = false) => {
    try {
      if (loadingState) setMobileRemindersLoading(true);
      const capUsd = Number(localStorage.getItem('ai_cap') || 50);
      const seen = mobileSeenReminderKeys.join(',');
      const data = await remindersAPI.list({ sinceMinutes: 180, capUsd, seen });
      if (data?.success) {
        setMobileReminders(Array.isArray(data.reminders) ? data.reminders : []);
      }
    } catch (_) {
      // keep mobile nav stable on API errors
    } finally {
      if (loadingState) setMobileRemindersLoading(false);
    }
  };

  React.useEffect(() => {
    try {
      localStorage.setItem('lyra_seen_reminders', JSON.stringify(mobileSeenReminderKeys.slice(0, 500)));
    } catch (_) {}
  }, [mobileSeenReminderKeys]);

  React.useEffect(() => {
    if (!isMobile) return;
    refreshMobileReminders(true);
    const timer = window.setInterval(() => {
      refreshMobileReminders(false);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [isMobile]);

  React.useEffect(() => {
    if (showMobileReminders) refreshMobileReminders(true);
  }, [showMobileReminders]);

  const markMobileReminderSeen = (eventKey: string) => {
    if (!eventKey) return;
    setMobileSeenReminderKeys((prev) => (prev.includes(eventKey) ? prev : [...prev, eventKey]));
    setMobileReminders((prev) => prev.filter((r) => r.eventKey !== eventKey));
  };

  const clearAllMobileReminders = () => {
    const keys = mobileReminders.map((r) => r.eventKey).filter(Boolean);
    if (keys.length === 0) return;
    setMobileSeenReminderKeys((prev) => [...prev, ...keys.filter((k) => !prev.includes(k))]);
    setMobileReminders([]);
  };

  const handleMobileReminderAction = async (reminder: any) => {
    if (!reminder) return;
    const stopAction = Array.isArray(reminder.actions)
      ? reminder.actions.find((a: any) => a?.type === 'stop_run' && a?.runId)
      : null;

    if (stopAction?.runId) {
      const runId = String(stopAction.runId);
      setMobileStoppingRunIds((prev) => {
        const next = new Set(prev);
        next.add(runId);
        return next;
      });
      try {
        await agentRunAPI.stop(runId);
      } catch (_) {
        // best effort
      } finally {
        setMobileStoppingRunIds((prev) => {
          const next = new Set(prev);
          next.delete(runId);
          return next;
        });
      }
    }

    if (reminder.link) {
      navigate(reminder.link);
      setShowMobileReminders(false);
      setIsMobileMenuOpen(false);
    }

    markMobileReminderSeen(reminder.eventKey);
  };

  const flatMenuPaths = React.useMemo(() => {
    const out: { key: string; label: string; path: string; icon: string }[] = [];
    const walk = (entries: [string, MenuItem][]) => {
      entries.forEach(([k, item]) => {
        if (item.visible && item.path && item.label) {
          out.push({ key: k, label: item.label, path: item.path, icon: item.icon || 'FileText' });
        }
        if (item.children) walk(Object.entries(item.children));
      });
    };
    walk(Object.entries(visibleMenuItems as Record<string, MenuItem>));
    return out;
  }, [visibleMenuItems]);

  const mobileMenuSearchResults = React.useMemo(() => {
    const q = mobileSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return flatMenuPaths
      .filter(item => item.label.toLowerCase().includes(q) || item.path.toLowerCase().includes(q))
      .slice(0, 10);
  }, [flatMenuPaths, mobileSearchQuery]);

  const sidebarContent = (
    <>
      <div className={`h-16 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center transition-all duration-300 ease-in-out ${isMenuCollapsed ? 'justify-center px-2' : 'justify-between px-3'}`}>
        {isMenuCollapsed ? (
          // Collapsed: a single centered button (logo doubles as the expand
          // control) — the logo + separate chevron combo used to overflow
          // the 80px collapsed width and get clipped by the sidebar's
          // overflow-hidden, which is why it visually flickered in/out.
          <button
            onClick={toggleMenuCollapsed}
            aria-label="Expand menu"
            title="Expand menu"
            className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-150 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <img src="/images/lyra-icon-compact.png" alt="Lyra" className="h-8 w-8" onError={(e: any) => { e.currentTarget.src = '/images/lyra-icon.png'; }} />
          </button>
        ) : (
          <>
            <div className="flex items-center gap-3 min-w-0">
              <img src="/images/lyra-icon.png" alt="Lyra logo" className="h-10 w-auto transition-all duration-300" />
              <h1 className={`text-lg font-semibold transition-all duration-300 tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Lyra</h1>
            </div>
            <button
              onClick={toggleMenuCollapsed}
              aria-label="Collapse menu"
              title="Collapse menu"
              className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 transition-colors duration-150 ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <ChevronLeft size={16} />
            </button>
          </>
        )}
      </div>

      <PrimarySidebarNav collapsed={isMenuCollapsed} isDarkMode={isDarkMode} onToggleDarkMode={toggleDarkMode} style={{ flex: 1, minHeight: 0 }} />
    </>
  );

  return (
    <div className={`flex h-screen overflow-x-hidden ${'bg-[color:var(--lyra-color-surface-2)]'}`}>
      {!isMobile && (
        <div
          className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} transition-all duration-300 ease-in-out ${isMenuCollapsed ? 'w-20' : 'w-[232px]'} flex flex-col overflow-hidden border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
          style={{ zIndex: 5, position: 'relative' }}
        >
          {sidebarContent}
        </div>
      )}

      {isMobile && (
        <>
          <div
            className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: 58,
              zIndex: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 14px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Open navigation"
                className={`${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} rounded`}
                style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <PanelLeft size={18} />
              </button>
              <img src="/images/lyra-icon-compact.png" alt="Lyra" style={{ width: 30, height: 30 }} onError={(e: any) => { e.currentTarget.src = '/images/lyra-icon.png'; }} />
              <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`} style={{ fontSize: 15, fontWeight: 700 }}>Lyra</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                ref={mobileCostBtnRef}
                onClick={() => setShowMobileCostPop(v => {
                  const nv = !v;
                  if (nv) fetchCostSummary();
                  return nv;
                })}
                className={`${'bg-[color:var(--app-primary-soft)] text-[color:var(--app-primary-deep)] border-[color:var(--app-primary-soft-2)]'} border`}
                style={{ borderRadius: 999, fontSize: 11, fontWeight: 700, padding: '4px 10px', lineHeight: 1.2 }}
                aria-label="Token usage"
                title="Token usage"
              >
                {!costHasData && costLoading ? '…' : 'Usage'}
              </button>
              <button
                onClick={() => navigate('/mission-control')}
                className={`${isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-gray-100 text-gray-700 border-gray-200'} border`}
                style={{ borderRadius: 999, fontSize: 11.5, fontWeight: 600, padding: '4px 10px', lineHeight: 1.2 }}
              >
                Runtime{runningCount > 0 ? ` ${runningCount}` : ''}
              </button>
              <button
                onClick={() => {
                  setShowMobileCostPop(false);
                  setShowMobileReminders((v) => !v);
                }}
                aria-label="Open reminders"
                title="Reminders"
                className={`${'bg-[color:var(--app-primary-soft)] text-[color:var(--app-primary-deep)] border-[color:var(--app-primary-soft-2)] hover:bg-[color:var(--app-surface-3)]'} border rounded`}
                style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <div style={{ position: 'relative', width: 16, height: 16 }}>
                  <Bell size={16} />
                  {mobileReminders.length > 0 ? (
                    <span
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -9,
                        minWidth: 14,
                        height: 14,
                        borderRadius: 999,
                        background: 'var(--lyra-color-danger)',
                        color: 'var(--app-on-accent)',
                        fontSize: 9,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 3px',
                        lineHeight: 1,
                      }}
                    >
                      {mobileReminders.length > 9 ? '9+' : mobileReminders.length}
                    </span>
                  ) : null}
                </div>
              </button>
            </div>
          </div>

          {showMobileReminders && (
            <div
              className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}
              style={{
                position: 'fixed',
                top: 62,
                right: 10,
                width: 'min(360px, calc(100vw - 20px))',
                borderRadius: 12,
                zIndex: 75,
                boxShadow: '0 12px 30px rgba(0,0,0,0.20)',
                maxHeight: '58vh',
                overflow: 'hidden',
              }}
            >
              <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} border-b`} style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className={`${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`} style={{ fontSize: 13, fontWeight: 700 }}>Reminders</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {mobileReminders.length > 0 && (
                    <button
                      onClick={clearAllMobileReminders}
                      className={`border ${isDarkMode ? 'text-red-400 border-gray-700 hover:bg-red-950/40' : 'text-red-600 border-red-200 bg-red-50 hover:bg-red-100'}`}
                      style={{ borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600 }}
                    >
                      Clear all
                    </button>
                  )}
                  <button
                    onClick={() => refreshMobileReminders(true)}
                    className={`border ${isDarkMode ? 'text-gray-300 border-gray-700 hover:bg-gray-700' : 'text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                    style={{ borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600 }}
                  >
                    Refresh
                  </button>
                </div>
              </div>
              <div style={{ maxHeight: 'calc(58vh - 40px)', overflowY: 'auto' }}>
                {mobileRemindersLoading ? (
                  <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} style={{ padding: '12px 10px', fontSize: 12 }}>Loading reminders...</div>
                ) : mobileReminders.length === 0 ? (
                  <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} style={{ padding: '12px 10px', fontSize: 12 }}>No reminders right now.</div>
                ) : (
                  mobileReminders.map((reminder, idx) => {
                    const stopAction = Array.isArray(reminder.actions)
                      ? reminder.actions.find((a: any) => a?.type === 'stop_run' && a?.runId)
                      : null;
                    const stopping = stopAction?.runId ? mobileStoppingRunIds.has(String(stopAction.runId)) : false;
                    return (
                      <div key={`${reminder.eventKey || 'm'}-${idx}`} className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} border-b last:border-b-0`} style={{ padding: '10px' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                          <div style={{ minWidth: 0 }}>
                            <div className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`} style={{ fontSize: 12.5, fontWeight: 700 }}>
                              {reminder.title || 'Reminder'}
                            </div>
                            <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} style={{ fontSize: 11.5, marginTop: 3 }}>
                              {reminder.message}
                            </div>
                          </div>
                          <button
                            onClick={() => markMobileReminderSeen(reminder.eventKey)}
                            className={`${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                            style={{ borderRadius: 6, padding: '2px 6px', fontSize: 10.5, height: 24, flexShrink: 0 }}
                          >
                            Dismiss
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          {stopAction ? (
                            <button
                              onClick={() => handleMobileReminderAction({ ...reminder, link: null })}
                              disabled={stopping}
                              className="text-white"
                              style={{
                                borderRadius: 7,
                                padding: '4px 8px',
                                fontSize: 11,
                                fontWeight: 700,
                                background: 'var(--lyra-color-danger)',
                                opacity: stopping ? 0.7 : 1,
                              }}
                            >
                              {stopping ? 'Stopping...' : 'Stop'}
                            </button>
                          ) : null}
                          {reminder.link ? (
                            <button
                              onClick={() => handleMobileReminderAction(reminder)}
                              className="text-white"
                              style={{ borderRadius: 7, padding: '4px 8px', fontSize: 11, fontWeight: 700, background: 'var(--app-primary-mid)' }}
                            >
                              Open
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {showMobileCostPop && mobileCostBtnRef.current && (
            <CostBudgetPopover
              anchorRef={mobileCostBtnRef}
              onClose={() => setShowMobileCostPop(false)}
              currency={currency}
              data={costData}
              loading={costLoading}
              showCurrencyToggle={isMobile}
              onToggleCurrency={() => setCurrency((c) => (c === 'USD' ? 'INR' : 'USD'))}
            />
          )}

          {isMobileMenuOpen && (
            <div
              onClick={() => setIsMobileMenuOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 70 }}
            />
          )}

          <aside
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              width: 288,
              maxWidth: '86vw',
              zIndex: 80,
              transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-105%)',
              transition: 'transform 220ms ease',
              boxShadow: '3px 0 24px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div className={`h-14 px-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src="/images/lyra-icon-compact.png" alt="Lyra" style={{ width: 28, height: 28 }} onError={(e: any) => { e.currentTarget.src = '/images/lyra-icon.png'; }} />
                <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`} style={{ fontSize: 14, fontWeight: 700 }}>Navigation</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close navigation"
                className={`${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'} rounded`}
                style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '10px 12px 8px', borderBottom: `1px solid ${'var(--lyra-color-border)'}` }}>
              {mobileSearchOpen && (
                <div>
                  <input
                    value={mobileSearchQuery}
                    onChange={(e) => setMobileSearchQuery(e.target.value)}
                    placeholder="Search pages..."
                    className={`${isDarkMode ? 'bg-gray-700 text-gray-100 border-gray-600 placeholder:text-gray-400' : 'bg-white text-gray-900 border-gray-300'} border`}
                    style={{
                      width: '100%',
                      borderRadius: 8,
                      padding: '8px 10px',
                      fontSize: 12,
                      outline: 'none',
                    }}
                  />
                  {mobileSearchQuery.trim().length > 0 && (
                    <div
                      className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}
                      style={{ marginTop: 6, borderRadius: 8, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}
                    >
                      {mobileMenuSearchResults.length === 0 ? (
                        <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ fontSize: 11, padding: '8px 10px' }}>
                          No pages found
                        </div>
                      ) : (
                        mobileMenuSearchResults.map((item) => {
                          const IconComponent = iconMap[item.icon] || iconMap.FileText;
                          return (
                            <button
                              key={`${item.path}-${item.key}`}
                              onClick={() => {
                                navigate(item.path);
                                setIsMobileMenuOpen(false);
                              }}
                              className={`${isDarkMode ? 'hover:bg-gray-700 text-gray-200 border-gray-700' : 'hover:bg-gray-50 text-gray-700 border-gray-100'}`}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderBottom: `1px solid ${'var(--app-surface-3)'}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontSize: 11,
                                textAlign: 'left',
                              }}
                            >
                              <IconComponent size={13} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <PrimarySidebarNav collapsed={false} isDarkMode={isDarkMode} onToggleDarkMode={toggleDarkMode} style={{ flex: 1, minHeight: 0 }} />

            <div
              className={`${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} border-t`}
              style={{ padding: '10px 12px 12px' }}
            >
              <button
                onClick={() => {
                  navigate('/settings/profile');
                  setIsMobileMenuOpen(false);
                }}
                className={`${isDarkMode ? 'hover:bg-gray-700 text-gray-100' : 'hover:bg-gray-50 text-gray-800'}`}
                style={{
                  width: '100%',
                  border: `1px solid ${'var(--lyra-color-border)'}`,
                  borderRadius: 10,
                  padding: '8px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  textAlign: 'left',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: isDarkMode ? 'var(--lyra-color-primary)' : '#1d4ed8',
                    color: 'var(--app-on-accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {formattedProfile?.initials || 'U'}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {userProfile?.name || 'Profile'}
                  </div>
                  <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ fontSize: 10.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {userProfile?.email || 'View profile settings'}
                  </div>
                </div>
                <User size={14} style={{ flexShrink: 0 }} />
              </button>

              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  if (onLogout) {
                    onLogout();
                    return;
                  }
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('googleAuthToken');
                  navigate('/login');
                }}
                className={`${isDarkMode ? 'text-red-300 hover:bg-red-900/30 border-red-700' : 'text-red-600 hover:bg-red-50 border-red-200'}`}
                style={{
                  width: '100%',
                  border: '1px solid',
                  borderRadius: 10,
                  padding: '8px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                <LogOut size={14} />
                <span>Logout</span>
              </button>
            </div>
          </aside>
        </>
      )}

      <div className="flex-1 overflow-hidden flex flex-col" style={{ paddingTop: isMobile ? 58 : 0 }}>
        {!isMobile && (
          <RuntimeDock
            tasks={activeRuns.map((r) => ({
              id: r._id,
              agentName: r.agentName || (r.chainId ? 'Workflow' : 'Agent'),
              status: 'running' as const,
              startedAt: r.createdAt,
              currentStep: r.agentName ? undefined : undefined,
            }))}
            onOpenMissionControl={() => navigate('/mission-control')}
            onStopTask={async (id: string) => {
              try {
                await stopRun(id);
              } catch (_) {}
              try {
                window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: { runId: id } }));
              } catch (_) {}
            }}
          />
        )}

        {runDrawerOpen && RunDrawer ? (
          <RunDrawer
            target={null}
            targetType="agent"
            onClose={() => {
              setRunDrawerOpen(false);
              setRunDrawerSpaceId(null);
              try {
                window.dispatchEvent(new CustomEvent('lyra:run-close'));
              } catch (_) {}
            }}
            gmailStatus={null}
            agents={runDrawerAgents}
            spaceId={runDrawerSpaceId}
          />
        ) : null}

        {!isMobile && TopBar ? (
          <TopBar
            isDarkMode={isDarkMode}
            onIconClick={handleTopBarIconClick}
            onSearchResults={handleSearchResults}
            onLogout={onLogout}
          />
        ) : null}

        <main className={`flex-1 overflow-y-auto ${'bg-[color:var(--lyra-color-surface)]'}`}>
          {children}
        </main>
      </div>
    </div>
  );
}
